import { type LoginResponse, LoginResponseSchema } from '@gestao/shared'

import { session } from './session'

/**
 * Sincroniza a sessão entre abas do mesmo navegador (BroadcastChannel, mesma origem).
 * Todas as abas dividem o mesmo cookie de refresh, então dividem também a sessão: quando uma
 * aba renova o token, as outras adotam o novo em vez de renovar de novo (renovar com o mesmo
 * cookie duas vezes conta como reuso e derruba todas as sessões do usuário).
 */
const CHANNEL_NAME = 'gestao:session'

type SyncMessage = { type: 'session'; data: LoginResponse } | { type: 'signed-out' }

let channel: BroadcastChannel | null = null

function onMessage(event: MessageEvent<unknown>): void {
  const message = event.data as Partial<SyncMessage> | null
  if (!message || typeof message !== 'object') return
  if (message.type === 'session') {
    const parsed = LoginResponseSchema.safeParse((message as { data?: unknown }).data)
    if (parsed.success) session.signIn(parsed.data)
    return
  }
  if (message.type === 'signed-out' && session.get().status === 'authenticated') {
    session.end('signed-out')
  }
}

/** Liga a sincronização (uma vez, na inicialização do app). Devolve a função que desliga. */
export function startSessionSync(): () => void {
  if (channel || typeof BroadcastChannel !== 'function') return () => undefined
  const current = new BroadcastChannel(CHANNEL_NAME)
  current.addEventListener('message', onMessage)
  channel = current
  return () => {
    current.removeEventListener('message', onMessage)
    current.close()
    if (channel === current) channel = null
  }
}

function post(message: SyncMessage): void {
  try {
    channel?.postMessage(message)
  } catch {
    // Canal fechado: as outras abas renovam sozinhas quando precisarem.
  }
}

export function broadcastSession(data: LoginResponse): void {
  post({ type: 'session', data })
}

export function broadcastSignOut(): void {
  post({ type: 'signed-out' })
}
